"use client";

import { useCallback } from "react";
import type { PricingPlanAccessInput } from "@/components/ui/Cpricingmodal/pricingModalTypes";
import { useStartCheckout } from "@/contexts/subscription";
import { discardOwnPostLoginIntentRequest } from "@/lib/postLoginIntent";
import {
  runPricingPlanAccess,
  runStartPlanCheckoutFlow,
} from "./homePageActions";
import type {
  HomePageModalStateLocal,
  StartPlanCheckoutFlowInput,
} from "./domain/home-page/types";

interface HomePagePricingHandlersLocal {
  /** Resolves once the plain gate is up — that is after this tab's own stale
   *  request has been withdrawn. */
  handleAuthorizeClick: () => Promise<void>;
  handleCloseAuthModal: () => void;
  handleClosePricingModal: () => void;
  handlePricingClick: () => void;
  /** Resolves once the plan is on its way — signed out, that is after the
   *  checkout intent is parked and the gate is up. */
  handlePricingPlanAccess: (input: PricingPlanAccessInput) => Promise<void>;
  startPlanCheckoutFlow: (input: StartPlanCheckoutFlowInput) => void;
}

interface UseHomePagePricingHandlersInput {
  hasActiveSubscription: boolean;
  isAuthenticated: boolean;
  modalState: Pick<
    HomePageModalStateLocal,
    | "pricingAccessLoadingPlanKey"
    | "setPricingAccessLoadingPlanKey"
    | "openAuthModal"
    | "closeAuthModal"
    | "setShowPricingModal"
  >;
  openUserMenu: () => void;
  startCheckout: ReturnType<typeof useStartCheckout>;
}

export function useHomePagePricingHandlers({
  hasActiveSubscription,
  isAuthenticated,
  modalState,
  openUserMenu,
  startCheckout,
}: UseHomePagePricingHandlersInput): HomePagePricingHandlersLocal {
  const {
    pricingAccessLoadingPlanKey,
    setPricingAccessLoadingPlanKey,
    openAuthModal,
    closeAuthModal,
    setShowPricingModal,
  } = modalState;

  const startPlanCheckoutFlow = useCallback(
    (input: StartPlanCheckoutFlowInput) => {
      runStartPlanCheckoutFlow({
        checkoutInput: input,
        hasActiveSubscription,
        openUserMenu,
        setPricingAccessLoadingPlanKey,
        closeAuthModal,
        setShowPricingModal,
        startCheckout,
      });
    },
    [
      hasActiveSubscription,
      openUserMenu,
      setPricingAccessLoadingPlanKey,
      closeAuthModal,
      setShowPricingModal,
      startCheckout,
    ],
  );

  const handlePricingClick = useCallback(() => {
    setShowPricingModal(true);
  }, [setShowPricingModal]);

  const handleAuthorizeClick = useCallback(async (): Promise<void> => {
    // A plain sign-in from the navbar carries no intent — drop THIS TAB's own
    // stale one so it cannot replay into a modal nobody asked for. The carrier
    // is cross-tab, so a blind clear here would also take down a request
    // another tab is still waiting on; a tab that parked nothing clears
    // nothing.
    //
    // AWAITED, for the mirror of the reason a park is: the withdrawal queues on
    // the carrier's record lock, and opening the gate over one still in the
    // queue would let a session land while the stale request is still there for
    // the landing to replay.
    await discardOwnPostLoginIntentRequest();
    openAuthModal();
  }, [openAuthModal]);

  const handleCloseAuthModal = useCallback(() => {
    closeAuthModal();
  }, [closeAuthModal]);

  const handleClosePricingModal = useCallback(() => {
    setShowPricingModal(false);
  }, [setShowPricingModal]);

  const handlePricingPlanAccess = useCallback(
    async (input: PricingPlanAccessInput): Promise<void> => {
      await runPricingPlanAccess({
        accessInput: input,
        isAuthenticated,
        pricingAccessLoadingPlanKey,
        openAuthModal,
        setShowPricingModal,
        startPlanCheckoutFlow,
      });
    },
    [
      isAuthenticated,
      pricingAccessLoadingPlanKey,
      openAuthModal,
      setShowPricingModal,
      startPlanCheckoutFlow,
    ],
  );

  return {
    handleAuthorizeClick,
    handleCloseAuthModal,
    handleClosePricingModal,
    handlePricingClick,
    handlePricingPlanAccess,
    startPlanCheckoutFlow,
  };
}
